
/* ============================== src/components/Modals/ProjectModal/ProjectFormFields.jsx ============================== */
/**
 * Поля формы проекта. Единый источник для ProjectModal (режим создания/редактирования)
 * и TemplateModal (режим шаблона). При isTemplate=true скрываются поля,
 * не имеющие смысла в шаблоне: code, start, end, contractNumber, hourlyRate,
 * status. Обязательность полей (required) в шаблоне отключается.
 */
import { FormField } from "../../FormField";
import { TagsField } from "../../TagsField";
import { TagsList } from "../../TagsList";
import { Ic } from "../../Icons";
import { ICONS } from "../../iconsData";
import { ENTITY_FIELDS_META } from "../../../utils/templateSchemas";
import { PROJECT_TYPES } from "../../../utils/constants";

const PROJECT_META = ENTITY_FIELDS_META.project;
const req = (f) => PROJECT_META[f].requiredInCreate === true;
const lab = (f) => PROJECT_META[f].label;

const PTYPE_OPTIONS = Object.freeze(
  Object.entries(PROJECT_TYPES).map(([k, v]) => ({ value: k, label: v })),
);

export function ProjectFormFields({
  form,
  access,
  options,
  aircraftFooter,
  onRequestHours,
  onRequestDeadline,
  isTemplate = false,
}) {
  const { values, handleChange, errors, touched } = form;
  const {
    canEditFields,
    isAdminProject,
    canChangeStatus,
    canEditContractNumber,
    canEditHourlyRate,
    canChangeManager,
    canSeeEconomicsFields,
  } = access;

  const {
    customerOptions,
    aircraftTypeOptions,
    categoryOptions,
    priorityOptions,
    unitOptions,
    employeeOptions,
    statusOptions,
    openTag,
  } = options;

  const hasTags = Array.isArray(values.tags) && values.tags.length > 0;

  return (
    <div className="project-info-fields">
      <FormField
        label={lab("name")}
        required={req("name") && !isTemplate}
        value={values.name ?? ""}
        onChange={(v) => handleChange("name", v)}
        error={touched.name && errors.name}
        disabled={!canEditFields}
        inline
      />

      <div className="fields-row fields-row--stacked">
        <div className="field-row">
          <label className="field-label">{lab("desc")}</label>
          <div className="flex-1">
            <textarea
              className="inp"
              rows={2}
              value={values.desc ?? ""}
              onChange={(e) => handleChange("desc", e.target.value)}
              disabled={!canEditFields}
            />
          </div>
        </div>
      </div>

      {canEditFields ? (
        <TagsField
          value={values.tagsInput ?? ""}
          onChange={(v) => handleChange("tagsInput", v)}
          error={touched.tagsInput && errors.tagsInput}
        />
      ) : (
        hasTags && (
          <div className="field-row">
            <label className="field-label">{lab("tags")}</label>
            <div className="flex-1">
              <TagsList
                tags={values.tags}
                onTagClick={openTag}
                interactive={false}
              />
            </div>
          </div>
        )
      )}

      <FormField
        label={lab("customer")}
        required={req("customer") && !isTemplate}
        type="select"
        editable
        options={customerOptions}
        value={values.customer ?? ""}
        onChange={(v) => handleChange("customer", v)}
        error={touched.customer && errors.customer}
        disabled={!canEditFields}
        placeholder="Поиск по заказчикам..."
        inline
      />

      <div className="fields-row">
        <FormField
          label={lab("ptype")}
          required={req("ptype") && !isTemplate}
          type="select"
          options={PTYPE_OPTIONS}
          value={values.ptype ?? "prod"}
          onChange={(v) => handleChange("ptype", v)}
          disabled={!canEditFields}
          inline
        />
        {!isTemplate && (
          <FormField
            label={lab("code")}
            required={req("code")}
            value={values.code ?? ""}
            onChange={(v) => handleChange("code", v)}
            error={touched.code && errors.code}
            disabled={!canEditFields}
            inline
          />
        )}
      </div>

      {!isAdminProject && (
        <div className="fields-row">
          <FormField
            label={lab("aircraftType")}
            required={!isTemplate}
            type="select"
            editable
            options={aircraftTypeOptions}
            value={values.aircraftType ?? ""}
            onChange={(v) => handleChange("aircraftType", v)}
            error={touched.aircraftType && errors.aircraftType}
            disabled={!canEditFields}
            footer={aircraftFooter}
            placeholder="Введите или выберите тип"
            inline
          />
          <FormField
            label={lab("projectType")}
            required={!isTemplate}
            type="select"
            editable
            options={categoryOptions}
            value={values.projectType ?? ""}
            onChange={(v) => handleChange("projectType", v)}
            error={touched.projectType && errors.projectType}
            disabled={!canEditFields}
            placeholder="Поиск по категориям..."
            inline
          />
        </div>
      )}

      <div className="fields-row">
        <FormField
          label={lab("priority")}
          required={req("priority") && !isTemplate}
          type="select"
          options={priorityOptions}
          value={values.priority ?? ""}
          onChange={(v) => handleChange("priority", v)}
          error={touched.priority && errors.priority}
          disabled={!canEditFields}
          inline
        />
        <FormField
          label={lab("unitIds")}
          required={req("unitIds") && !isAdminProject && !isTemplate}
          type="select"
          multiple
          options={unitOptions}
          value={values.unitIds ?? []}
          onChange={(v) => handleChange("unitIds", v)}
          error={touched.unitIds && errors.unitIds}
          disabled={!canEditFields}
          inline
        />
      </div>

      {!isTemplate && (
        <div className="fields-with-action">
          <div className="fields-row">
            <FormField
              label={lab("start")}
              required={req("start")}
              type="date"
              value={values.start ?? ""}
              onChange={(v) => handleChange("start", v)}
              error={touched.start && errors.start}
              disabled={!canEditFields}
              inline
            />
            <FormField
              label={lab("end")}
              required={req("end") && !isAdminProject}
              type="date"
              value={values.end ?? ""}
              onChange={(v) => handleChange("end", v)}
              error={touched.end && errors.end}
              disabled={!canEditFields}
              inline
            />
          </div>
          {onRequestDeadline && (
            <div className="field-action-row">
              <button
                type="button"
                className="btn request-hours field-action"
                onClick={onRequestDeadline}
              >
                <Ic d={ICONS.cal} size={14} /> Запросить изменение срока
              </button>
            </div>
          )}
        </div>
      )}

      <div className="fields-with-action">
        <div className="fields-row">
          {!isTemplate && (
            <FormField
              label="Статус"
              required
              type="select"
              options={statusOptions}
              value={values.status ?? ""}
              onChange={(v) => handleChange("status", v)}
              error={touched.status && errors.status}
              disabled={!canChangeStatus}
              inline
            />
          )}
          <FormField
            label={lab("plannedHours")}
            required={req("plannedHours") && !isAdminProject && !isTemplate}
            type="number"
            min="0"
            step="0.5"
            value={values.plannedHours ?? ""}
            onChange={(v) => handleChange("plannedHours", v)}
            error={touched.plannedHours && errors.plannedHours}
            disabled={!canEditFields}
            inline
          />
        </div>
        {onRequestHours && (
          <div className="field-action-row">
            <button
              type="button"
              className="btn request-hours field-action"
              onClick={onRequestHours}
            >
              <Ic d={ICONS.clock} size={14} /> Запрос изменения часов
            </button>
          </div>
        )}
      </div>

      {!isTemplate && !isAdminProject && canSeeEconomicsFields && (
        <div className="fields-row">
          <FormField
            label={lab("contractNumber")}
            value={values.contractNumber ?? ""}
            onChange={(v) => handleChange("contractNumber", v)}
            error={touched.contractNumber && errors.contractNumber}
            disabled={!canEditContractNumber}
            inline
          />
          <FormField
            label={lab("hourlyRate")}
            type="number"
            min="0"
            step="0.01"
            value={values.hourlyRate ?? ""}
            onChange={(v) => handleChange("hourlyRate", v)}
            error={touched.hourlyRate && errors.hourlyRate}
            disabled={!canEditHourlyRate}
            inline
          />
        </div>
      )}

      <FormField
        label={lab("managerId")}
        required={req("managerId") && !isAdminProject && !isTemplate}
        type="select"
        options={employeeOptions}
        value={values.managerId ?? ""}
        onChange={(v) => handleChange("managerId", v)}
        error={touched.managerId && errors.managerId}
        disabled={!canChangeManager}
        inline
      />
    </div>
  );
}
